import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { ColorEditorPopover } from '@/components/feature-configs/autoplay/ColorEditorPopover';
import { ContinueWatchingOverlay } from '@/store/videoFeatures';
import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ContinueWatchingCustomizeTabProps {
  overlay: ContinueWatchingOverlay;
  onOverlayChange: (updates: Partial<ContinueWatchingOverlay>) => void;
}

export const ContinueWatchingCustomizeTab: React.FC<ContinueWatchingCustomizeTabProps> = ({
  overlay,
  onOverlayChange
}) => {
  useLanguage();
  const autoResume = overlay.autoResume || { enabled: false, delay: 3, showCountdown: true };
  const soundAlert = overlay.soundAlert || { enabled: false, type: 'click', volume: 50 };
  
  return (
    <div className="space-y-6 py-2">
        {/* GRUPO 1: Conteúdo do Overlay */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">{translateInterfaceText("Conteúdo de Continuar de onde parou")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">{translateInterfaceText("Título do overlay")}</Label>
              <Input
                value={overlay.message || ''}
                onChange={(e) => onOverlayChange({ message: e.target.value })}
                placeholder={translateInterfaceText("Você já começou a assistir esse vídeo")}
                className="h-10 rounded-xl w-full max-w-[460px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">{translateInterfaceText("Texto do botão principal")}</Label>
                <Input
                  value={overlay.continueText || ''}
                  onChange={(e) => onOverlayChange({ continueText: e.target.value })}
                  placeholder={translateInterfaceText("Continuar assistindo?")}
                  className="h-10 rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">{translateInterfaceText("Texto do texto secundário")}</Label>
                <Input
                  value={overlay.restartText || ''}
                  onChange={(e) => onOverlayChange({ restartText: e.target.value })}
                  placeholder={translateInterfaceText("Assistir do início")}
                  className="h-10 rounded-xl"
                />
              </div>
            </div>
          </CardContent>
        </Card>

      {/* GRUPO 2: Aparência */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">{translateInterfaceText("Aparência")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Cores do Overlay - 2 bolinhas em linha */}
          <div className="space-y-3">
            <h3 className="text-[14px] font-medium text-[#475569]">{translateInterfaceText("Cores do Overlay")}</h3>

            <div className="flex items-center gap-6">
              <div className="flex flex-col items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground font-medium">{translateInterfaceText("Fundo")}</span>
                <ColorEditorPopover
                  colorName={translateInterfaceText("Fundo do overlay")}
                  currentColor={overlay.backgroundColor || '#2563EB'}
                  onSave={(color) => onOverlayChange({ backgroundColor: color })}
                >
                  <button
                    type="button"
                    className="w-10 h-10 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform cursor-pointer"
                    style={{ backgroundColor: overlay.backgroundColor || '#2563EB' }}
                  />
                </ColorEditorPopover>
              </div>

              <div className="flex flex-col items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground font-medium">{translateInterfaceText("Texto")}</span>
                <ColorEditorPopover
                  colorName={translateInterfaceText("Cor do texto")}
                  currentColor={overlay.textColor || '#ffffff'}
                  onSave={(color) => onOverlayChange({ textColor: color })}
                >
                  <button
                    type="button"
                    className="w-10 h-10 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform cursor-pointer"
                    style={{ backgroundColor: overlay.textColor || '#ffffff' }}
                  />
                </ColorEditorPopover>
              </div>

              {overlay.template !== 'continueSmall' && (
                <>
                  <div className="flex flex-col items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground font-medium">{translateInterfaceText("Fundo botão play")}</span>
                    <ColorEditorPopover
                      colorName={translateInterfaceText("Fundo botão play")}
                      currentColor={overlay.playButtonBgColor || 'transparent'}
                      onSave={(color) => onOverlayChange({ playButtonBgColor: color })}
                    >
                      <button
                        type="button"
                        className="w-10 h-10 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform cursor-pointer"
                        style={{ backgroundColor: overlay.playButtonBgColor || 'transparent' }}
                      />
                    </ColorEditorPopover>
                  </div>

                  <div className="flex flex-col items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground font-medium">{translateInterfaceText("Fundo botão restart")}</span>
                    <ColorEditorPopover
                      colorName={translateInterfaceText("Fundo botão restart")}
                      currentColor={overlay.restartButtonBgColor || 'transparent'}
                      onSave={(color) => onOverlayChange({ restartButtonBgColor: color })}
                    >
                      <button
                        type="button"
                        className="w-10 h-10 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform cursor-pointer"
                        style={{ backgroundColor: overlay.restartButtonBgColor || 'transparent' }}
                      />
                    </ColorEditorPopover>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
